import type { ReactNode } from 'react';
import { create } from 'zustand';
import type { Order } from '@/api/spec';
import type { DateRangeTuple } from '@/components/table/common/date_filter';
import type { ViewOrder } from '@/feature/order_entry/ViewOrder';
import type { CsvExportSource } from '@/feature/terminal_tables/csv_export/csvExport';
import type { ViewPosition } from '../../feature/terminal_tables/position/types';

export interface ConfirmationDialogData {
  title: ReactNode;
  content: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  isDestructive?: boolean;
  showDontShowAgain?: boolean;
  onDontShowAgain?: (dontShowAgain: boolean) => void;
}

export type DialogKey =
  | 'depositCollateralDialog'
  | 'accessRequestDialog'
  | 'geoBlockDialog'
  | 'accountDialog'
  | 'termsDialog'
  | 'oneClickTradingDialog'
  | 'withdrawDialog'
  | 'tpSlDialog'
  | 'adjustMarginDialog'
  | 'adjustLeverage'
  | 'sharePnLDialog'
  | 'tournamentDialog'
  | 'tournamentFinalisingDialog'
  | 'closePositionDialog'
  | 'flipPositionDialog'
  | 'addReferralCodeDialog'
  | 'shareReferralDialog'
  | 'confirmationDialog'
  | 'badgesDialog'
  | 'sharePointsDialog'
  | 'slippageDialog'
  | 'feeTiersDialog'
  | 'ogBadgeDialog'
  | 'editOrderDialog'
  | 'userPreferencesDrawer'
  | 'editAliasDialog'
  | 'rewardsStructureDialog'
  | 'exportCsvDialog'
  | 'keyboardShortcutsDialog';

/**
 * Result handed back from the close-position dialog to the confirm handler.
 * `closeSize` (exact asset units) is the source of truth for single-position partial closes;
 * `sizePercentage` is the integer display percent (close-all always uses 100). `limitPrice`
 * overrides the mid-price default for a limit close.
 */
export interface CloseConfirmOptions {
  useLimit: boolean;
  sizePercentage: number;
  closeSize?: number;
  limitPrice?: number;
}

export interface ClosePositionDialogData {
  // Present for single-position closes; absent for close-all. Drives the rich interactive variant.
  position?: ViewPosition;
  isCloseAll?: boolean;
  positionCount?: number;
  closeOrdersCount?: number;
  autoCancelsCloseOrders?: boolean;
  onConfirm: (options: CloseConfirmOptions) => void;
}

export interface FlipPositionDialogData {
  position: ViewPosition;
  closeOrdersCount: number;
  autoCancelsCloseOrders: boolean;
  onConfirm: () => void;
}

export interface OGBadgeDialogData {
  badge: {
    name: string;
    image: string;
  };
  awardedAt?: number;
}

export interface EditOrderDialogData {
  viewOrder: ViewOrder;
  apiOrder: Order;
}

export interface GeoBlockDialogData {
  countryCode: string;
}

export interface SlippageDialogData {
  currentSlippage?: number;
  defaultSlippage?: number;
  // Receives the persisted value: null when the user picked the backend default (follow-default).
  onConfirm?: (slippagePercent: number | null) => void;
}

export interface FeeTiersDialogData {
  // Per-tier fee ladders of the selected market, index = tier (0 = base).
  makerFeesMicros: bigint[];
  takerFeesMicros: bigint[];
  chargesCloseFee: boolean; // false keeps the "open only" note
  // Account fee tier (Account.ft) — highlights the user's row; undefined when not signed in.
  feeTier?: number;
}

export interface ExportCsvDialogData {
  source: CsvExportSource;
  tableName: string;
  initialRange: DateRangeTuple;
}

export interface DialogDataMap {
  confirmationDialog: ConfirmationDialogData;
  accountDialog: undefined;
  closePositionDialog: ClosePositionDialogData;
  flipPositionDialog: FlipPositionDialogData;
  geoBlockDialog: GeoBlockDialogData | undefined;
  slippageDialog: SlippageDialogData | undefined;
  feeTiersDialog: FeeTiersDialogData;
  ogBadgeDialog: OGBadgeDialogData | undefined;
  editOrderDialog: EditOrderDialogData;
  exportCsvDialog: ExportCsvDialogData;
}

type DialogArgs<K extends DialogKey> = K extends keyof DialogDataMap
  ? DialogDataMap[K] extends undefined
    ? []
    : undefined extends DialogDataMap[K]
      ? [data?: DialogDataMap[K]]
      : [data: DialogDataMap[K]]
  : [];

interface DialogStoreState {
  // holds the open state of multiple dialogs
  openDialogs: Partial<Record<DialogKey, boolean>>;
  dialogData: { [K in DialogKey]?: K extends keyof DialogDataMap ? DialogDataMap[K] : undefined };
  // actions
  openDialog: <K extends DialogKey>(dialogKey: K, ...args: DialogArgs<K>) => void;
  closeDialog: (dialogKey: DialogKey) => void;
  // selectors
  isDialogOpen: (dialogKey: DialogKey) => boolean;
  getDialogData: <K extends DialogKey>(
    dialogKey: K
  ) => K extends keyof DialogDataMap ? DialogDataMap[K] | undefined : undefined;
}

export const useDialogStore = create<DialogStoreState>((set, get) => ({
  openDialogs: {},
  dialogData: {},
  openDialog: (dialogKey, ...args) =>
    set((state) => ({
      openDialogs: { ...state.openDialogs, [dialogKey]: true },
      dialogData: { ...state.dialogData, [dialogKey]: args[0] as any },
    })),
  closeDialog: (dialogKey) =>
    set((state) => {
      const newDialogData = { ...state.dialogData };
      delete newDialogData[dialogKey];
      return {
        openDialogs: { ...state.openDialogs, [dialogKey]: false },
        dialogData: newDialogData,
      };
    }),
  isDialogOpen: (dialogKey) => !!get().openDialogs[dialogKey],
  getDialogData: (dialogKey) => get().dialogData[dialogKey] as any,
}));
